import type { CookieBannerProps } from "@/types/cookie-banner/props";
import type { CookieConsentConfiguration } from "@/types/cookie-consent-configuration";

import * as CookieConsent from "vanilla-cookieconsent";

import { useCallback, useEffect, useMemo } from "react";

import { useCookieStyles } from "./use-cookie-styles";
import { defaultConfiguration } from "@/constants/configuration";
import { useSetupFirebase } from "@/functions/firebase";
import { useUniqueUserId } from "@/hooks/use-unique-user-id";
import { deepMerge } from "@/functions/deep-merge";

export function useCookieBanner({
  activeTheme = "light",
  configuration,
  delay = 1000,
  firebase,
  styles,
}: CookieBannerProps = {}) {
  const userId = useUniqueUserId();

  const { onChange, onFirstConsent, ...options } = useMemo(() => {
    return deepMerge<CookieConsentConfiguration>(
      defaultConfiguration,
      configuration
    );
  }, [configuration]);

  const { retrieveUserCookieData, updateUserCookieData } =
    useSetupFirebase(firebase);

  const logConsent = useCallback(() => {
    const cookie = CookieConsent.getCookie();
    const preferences = CookieConsent.getUserPreferences();

    if (firebase?.apiKey) {
      updateUserCookieData({
        cookie,
        language: options.language.default,
        preferences,
        userId,
      });
    }
  }, [
    firebase?.apiKey,
    options.language.default,
    updateUserCookieData,
    userId,
  ]);

  const updateUserId = useCallback(
    async (newUserId: string, onError?: (e: unknown) => void) => {
      if (firebase?.apiKey) {
        await updateUserCookieData({ userId: newUserId }, userId, onError);
      }
    },
    [firebase?.apiKey, userId, updateUserCookieData]
  );

  useEffect(() => {
    CookieConsent.run({
      ...options,
      onChange: (param) => {
        logConsent();
        onChange?.(param);
      },
      onFirstConsent: (param) => {
        logConsent();
        onFirstConsent?.(param);
      },
    });

    const timeoutId = setTimeout(CookieConsent.show, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [options, delay, logConsent, onFirstConsent, onChange]);

  useEffect(() => {
    CookieConsent.setCookieData({
      mode: "update",
      value: { userId },
    });
  }, [userId]);

  useCookieStyles(activeTheme, styles);

  return { retrieveUserCookieData, updateUserCookieData, updateUserId, userId };
}
